import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import ChargeModal from './components/ChargeModal';
import DonationModal from './components/DonationModal';
import GiftModal from './components/GiftModal';
import CouponModal from './components/CouponModal';
import MissionModal from './components/MissionModal';
import WithdrawModal from './components/WithdrawModal';
import StatsModal from './components/StatsModal';
import EarnModal from './components/EarnModal';
import SpendModal from './components/SpendModal';
import { useNavigate } from 'react-router-dom';

export default function TalentPage() {
  const navigate = useNavigate();
  const { user, profile, loading, isAdminAsMember } = useAuth();
  const [talentPoints, setTalentPoints] = useState<TalentPoints | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [showMissionModal, setShowMissionModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showEarnModal, setShowEarnModal] = useState(false);
  const [showSpendModal, setShowSpendModal] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');

  useEffect(() => {
    if (!loading && !user && !isAdminAsMember) {
      navigate('/auth/login', { state: { from: { pathname: '/talent' } } });
      return;
    }

    if ((user || isAdminAsMember) && profile) {
      loadTalentData();
    }
  }, [user, profile, loading, navigate, isAdminAsMember]);

  const loadTalentData = async () => {
    if (!profile?.id) return;

    // 통합관리자는 가상 데이터 표시
    if (isAdminAsMember) {
      setTalentPoints({
        id: 'admin-mock',
        user_id: 'admin-mock-id',
        balance: 999999,
        total_earned: 999999,
        total_spent: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      setTransactions([]);
      setLoadingData(false);
      return;
    }

    try {
      // 달란트 포인트 조회
      const { data: pointData, error: pointError } = await supabase
        .from('talent_points')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (pointError) throw pointError;

      setTalentPoints(pointData);
      setTransactions([]);

      // 거래 내역 조회
      const { data: transactionData, error: transactionError } = await supabase
        .from('point_transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (transactionError) throw transactionError;

      setTransactions(transactionData || []);
    } catch (error) {
      console.error('달란트 데이터 로드 실패:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      earn: '적립',
      spend: '사용',
      exchange: '환급',
      reward: '보상',
    };
    return labels[type] || type;
  };

  const getTypeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      earn: 'text-green-600 bg-green-50',
      spend: 'text-red-600 bg-red-50',
      exchange: 'text-blue-600 bg-blue-50',
      reward: 'text-purple-600 bg-purple-50',
    };
    return colors[type] || 'text-gray-600 bg-gray-50';
  };

  const filteredTransactions = filterType === 'all'
    ? transactions
    : transactions.filter((t: any) => t.type === filterType);

  if (!user && !isAdminAsMember) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">달란트</h1>
            <p className="text-lg text-gray-600">신앙 활동으로 적립하고 사용하는 포인트</p>
          </div>

          {!user ? (
            <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
              <i className="ri-lock-line text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">로그인이 필요합니다</h3>
              <p className="text-gray-600 mb-6">달란트 내역을 확인하려면 로그인해주세요</p>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/auth/login')}
                className="px-8 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                로그인하기
              </button>
            </div>
          ) : loadingData ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-teal-600 border-t-transparent"></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-6 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-teal-100 text-sm font-medium">보유 달란트</span>
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                      <i className="ri-coin-line text-xl"></i>
                    </div>
                  </div>
                  <p className="text-4xl font-bold mb-2">{talentPoints?.balance?.toLocaleString() || 0}</p>
                  <p className="text-teal-100 text-sm">사용 가능한 포인트</p>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-gray-600 text-sm font-medium">총 적립</span>
                    <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                      <i className="ri-arrow-down-line text-xl text-green-600"></i>
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-gray-900 mb-2">{talentPoints?.total_earned?.toLocaleString() || 0}</p>
                  <p className="text-gray-500 text-sm">누적 적립 포인트</p>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-gray-600 text-sm font-medium">총 사용</span>
                    <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                      <i className="ri-arrow-up-line text-xl text-red-600"></i>
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-gray-900 mb-2">{talentPoints?.total_spent?.toLocaleString() || 0}</p>
                  <p className="text-gray-500 text-sm">누적 사용 포인트</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                <button
                  onClick={() => setShowEarnModal(true)}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 text-white py-4 px-6 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-circle-line text-xl"></i>
                  달란트 적립
                </button>
                <button
                  onClick={() => setShowSpendModal(true)}
                  className="bg-gradient-to-r from-red-500 to-rose-500 text-white py-4 px-6 rounded-xl font-semibold hover:from-red-600 hover:to-rose-600 transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-subtract-line text-xl"></i>
                  달란트 사용
                </button>
                <button
                  onClick={() => setShowChargeModal(true)}
                  className="bg-white border-2 border-teal-600 text-teal-600 py-4 px-6 rounded-xl font-semibold hover:bg-teal-50 transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-wallet-line text-xl"></i>
                  충전하기
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <button
                  onClick={() => setShowDonationModal(true)}
                  className="bg-teal-600 text-white py-4 px-6 rounded-xl font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-heart-line text-xl"></i>
                  기부·헌금
                </button>
                <button
                  onClick={() => setShowGiftModal(true)}
                  className="bg-white border-2 border-purple-600 text-purple-600 py-4 px-6 rounded-xl font-semibold hover:bg-purple-50 transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-gift-line text-xl"></i>
                  선물하기
                </button>
                <button
                  onClick={() => setShowWithdrawModal(true)}
                  className="bg-white border-2 border-blue-600 text-blue-600 py-4 px-6 rounded-xl font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-bank-card-line text-xl"></i>
                  환급신청
                </button>
                <button
                  onClick={() => setShowStatsModal(true)}
                  className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-4 px-6 rounded-xl font-semibold hover:from-indigo-600 hover:to-purple-600 transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-bar-chart-line text-xl"></i>
                  통계 보기
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-2 gap-4 mb-8">
                <button
                  onClick={() => setShowMissionModal(true)}
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white py-4 px-6 rounded-xl font-semibold hover:from-yellow-600 hover:to-orange-600 transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-trophy-line text-xl"></i>
                  미션 완료하기
                </button>
                <button
                  onClick={() => setShowCouponModal(true)}
                  className="bg-gradient-to-r from-pink-500 to-rose-500 text-white py-4 px-6 rounded-xl font-semibold hover:from-pink-600 hover:to-rose-600 transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-coupon-3-line text-xl"></i>
                  쿠폰 등록
                </button>
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">거래 내역</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setFilterType('all')}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                        filterType === 'all'
                          ? 'bg-teal-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      전체
                    </button>
                    <button
                      onClick={() => setFilterType('earn')}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                        filterType === 'earn'
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      적립
                    </button>
                    <button
                      onClick={() => setFilterType('spend')}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                        filterType === 'spend'
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      사용
                    </button>
                    <button
                      onClick={() => setFilterType('reward')}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                        filterType === 'reward'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      보상
                    </button>
                  </div>
                </div>

                {filteredTransactions.length === 0 ? (
                  <div className="text-center py-12">
                    <i className="ri-file-list-line text-6xl text-gray-300 mb-4"></i>
                    <p className="text-gray-500">거래 내역이 없습니다</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredTransactions.map((transaction: any) => (
                      <div
                        key={transaction.id}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${getTypeColor(transaction.type)}`}>
                              {getTypeLabel(transaction.type)}
                            </span>
                            <span className="text-sm text-gray-500">
                              {new Date(transaction.created_at).toLocaleDateString('ko-KR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>
                          <p className="text-gray-900">{transaction.description}</p>
                        </div>
                        <div className={`text-lg font-bold ${transaction.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {transaction.amount > 0 ? '+' : ''}{transaction.amount.toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {showChargeModal && (
        <ChargeModal
          onClose={() => setShowChargeModal(false)}
          onSuccess={loadTalentData}
        />
      )}

      {showDonationModal && (
        <DonationModal
          onClose={() => setShowDonationModal(false)}
          onSuccess={loadTalentData}
        />
      )}

      {showGiftModal && (
        <GiftModal
          onClose={() => setShowGiftModal(false)}
          onSuccess={loadTalentData}
        />
      )}

      {showCouponModal && (
        <CouponModal
          onClose={() => setShowCouponModal(false)}
          onSuccess={loadTalentData}
        />
      )}

      {showMissionModal && (
        <MissionModal
          onClose={() => setShowMissionModal(false)}
          onSuccess={loadTalentData}
        />
      )}

      {showWithdrawModal && (
        <WithdrawModal
          onClose={() => setShowWithdrawModal(false)}
          onSuccess={loadTalentData}
        />
      )}

      {showStatsModal && (
        <StatsModal
          onClose={() => setShowStatsModal(false)}
        />
      )}

      {showEarnModal && (
        <EarnModal
          onClose={() => setShowEarnModal(false)}
          onSuccess={loadTalentData}
        />
      )}

      {showSpendModal && (
        <SpendModal
          onClose={() => setShowSpendModal(false)}
          onSuccess={loadTalentData}
          currentBalance={talentPoints?.balance || 0}
        />
      )}

      <Footer />
    </div>
  );
}
